import type { GetPublicArticleDetailByPermalinkQueryArticleExtended } from '@shared/types/kiwiUserServer';
import type { WriterUrl } from '@shared/types/Writer';
import type { NewsArticleContentJSONData } from '@shared/types/Article';

export function useTransformArticle(
  article: GetPublicArticleDetailByPermalinkQueryArticleExtended,
) {
  const writerUrls = computed<WriterUrl[]>(() => {
    if (!article.writer?.urls_json) {
      return [];
    }

    try {
      return JSON.parse(article.writer?.urls_json).urls;
    } catch {
      return [];
    }
  });

  // NOTE: articleContentsの「data」カラムにstringifyされたJSONが格納されているので、parseしてobjectとして使えるように変換しておく
  const parsedContents = computed(() => {
    return article.articleContents.map((content) => {
      const data: {
        sections: NewsArticleContentJSONData[];
      } = (() => {
        if (!content.data) {
          return {
            sections: [],
          };
        }

        try {
          return JSON.parse(content.data);
        } catch {
          return {
            sections: [],
          };
        }
      })();

      return {
        ...content,
        data,
      };
    });
  });

  // NOTE: articleContentsにページ番号のカラムと(page)、そのページ内で何番目の章か(pageSection)、を追加したもの
  const contentsWithPage = computed(() => {
    let currentPage = 1;
    let currentPageSection = 1;

    return parsedContents.value
      .sort((a, b) => a.sortorder - b.sortorder)
      .map((raw) => {
        const result = {
          ...raw,
          page: currentPage,
          pageSection: currentPageSection,
        };

        currentPageSection++;

        if (raw.page_feed) {
          currentPage++;
          currentPageSection = 1;
        }
        return result;
      });
  });

  // NOTE: コンテンツをページ毎に分けて配列に格納したもの
  const paginatedContents = computed(() => {
    const result: (typeof contentsWithPage.value)[] = [];

    contentsWithPage.value.forEach((raw) => {
      const page = raw.page - 1;
      if (!result[page]) {
        result[page] = [];
      }
      result[page].push(raw);
    });
    return result;
  });

  return {
    writerUrls,
    parsedContents,
    contentsWithPage,
    paginatedContents,
  };
}
